<script setup lang="ts">
  import { useSubAppStore } from '@/store/sub-app.store'
  import {
    ChevronRight,
    DropDown,
    FormFieldContainer,
    FormInput,
    UiButton,
    LoadingSpinner,
    FileUpload,
    SwitchButton,
    require,
    isUuid,
    useXPathAction,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { useRoute, useRouter } from 'vue-router'
  import { useFlowConfigStore } from '@/store/flow-config.store'
  import { useSubAppValidation } from '@/composables/validation/use-sub-app-validation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useCustomerStore } from '@/store/customer.store'
  import { useFileStore } from '@/store/file.store'
  import type { Flow } from '@/types/flow-config-type'
  import { createEmptySubAppPayload, type SubAppPayload } from '@/types/sub-app-types'
  import type { Customer } from '@/types/nest/customer'
  import { isBlobDifferentFromFile } from '@/utils/file-utils'
  import { withDefaultColours } from '@/composables/use-sub-app-colours'
  import { defaultFlows } from '@/configs/default-flows'
  import NewFlowModal from '@/components/form-wizard/NewFlowModal.vue'
  import SubAppThemeModal from '@/components/form-wizard/SubAppThemeModal.vue'
  import TopBar from '@/components/TopBar.vue'
  import { isAxiosError } from 'axios'
  import ImportFlowModal from '@/components/form-wizard/ImportFlowModal.vue'
  import TranslationsModal from '@/components/form-wizard/TranslationsModal.vue'

  const subAppStore = useSubAppStore()
  const flowConfigStore = useFlowConfigStore()
  const customerStore = useCustomerStore()
  const xPathAction = useXPathAction()
  const fileStore = useFileStore()

  const router = useRouter()
  const route = useRoute()
  const toastStore = useToastStore()

  const localSubApp = ref<SubAppPayload>(createEmptySubAppPayload())
  const { visibleErrors, validate, isSubmittedAndInvalid } = useSubAppValidation(localSubApp)

  const isLoadingPreview = ref(false)
  const hasChanges = ref(false)
  const originalPayload = ref<SubAppPayload | null>(null)
  const selectedCustomer = ref<Customer | null>(null)

  const availableFlows = ref<Flow[]>([...defaultFlows])
  const showNewFlowModal = ref<boolean>(false)
  const showImportFlowModal = ref<boolean>(false)
  const showSubAppThemeModal = ref<boolean>(false)
  const showTranslationsModal = ref<boolean>(false)
  const hasTranslations = ref<boolean>(false)

  const homeConfig = ref<{ widget: string }[]>([])
  const menuConfig = ref<{ widget: string }[]>([])

  const isDisabled = computed(() => localSubApp.value.intent || route.params['subAppId'] === 'new')
  const hasImage = computed(() => localSubApp.value.image || localSubApp.value.imageSrc)
  const isSubmitDisabled = computed(() => isSubmittedAndInvalid.value || !hasChanges.value)

  async function submitSubApp(): Promise<void> {
    if (!validate()) return

    const subAppChanged = hasChanges.value
    const flowColoursChanged = flowConfigStore.hasFlowColourChanges

    if (!subAppChanged && !flowColoursChanged) return

    try {
      await Promise.all([
        subAppChanged
          ? localSubApp.value.id && localSubApp.value.image
            ? onUpdate()
            : onCreate()
          : Promise.resolve(),
        flowColoursChanged ? saveFlowColours() : Promise.resolve(),
      ])
    } catch (error) {
      throw error
    }
  }

  async function saveFlowColours() {
    try {
      await flowConfigStore.saveChangedFlowColours()
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error(error.response?.data ?? error.response)
      } else {
        toastStore.error('Er is iets fout gegaan. Probeer later opnieuw.')
      }
      throw error
    }
  }

  async function onUpdate() {
    const imageChanged =
      !originalPayload.value?.image ||
      (await isBlobDifferentFromFile(originalPayload.value.image, localSubApp.value.image!))

    if (imageChanged) {
      await fileStore.updateFile(localSubApp.value.image!, localSubApp.value.imageId)
      originalPayload.value!.image = localSubApp.value.image
    } else {
      localSubApp.value.image = originalPayload.value!.image
    }

    try {
      await subAppStore.updateSubApp(localSubApp.value)

      originalPayload.value = {
        ...localSubApp.value,
        homeConfig: [...localSubApp.value.homeConfig],
        menuConfig: [...localSubApp.value.menuConfig],
        cssVariables: { ...localSubApp.value.cssVariables },
      }
      hasChanges.value = false
      toastStore.success(`Sub app updated successfully.`)
    } catch (error) {
      if (isAxiosError(error)) {
        toastStore.error(error.response?.data ?? error.response)
      } else {
        toastStore.error('Er is iets fout gegaan. Probeer later opnieuw.')
      }
      throw error
    }
  }

  async function onCreate() {
    const file = await fileStore.uploadFile(localSubApp.value.image!)
    const result = await subAppStore.createSubApp(localSubApp.value, file.id)

    localSubApp.value = {
      ...result,
      cssVariables: withDefaultColours(result.cssVariables),
      image: localSubApp.value.image,
    }
    isLoadingPreview.value = true

    const { imageSrc } = await fileStore.downloadFile(file.id)

    fileStore.imagePreviewUrl = imageSrc

    await new Promise(resolve => setTimeout(resolve, 1000))
    isLoadingPreview.value = false

    originalPayload.value = {
      ...localSubApp.value,
      homeConfig: [...localSubApp.value.homeConfig],
      menuConfig: [...localSubApp.value.menuConfig],
      cssVariables: { ...localSubApp.value.cssVariables },
    }

    hasChanges.value = false
    toastStore.success(`Sub app added successfully.`)

    await router.replace({
      name: 'sub-app-details',
      params: {
        subAppId: result.id,
      },
    })
  }

  async function onSelect(flow: Flow) {
    const subAppId = route.params['subAppId']
    if (!isUuid(subAppId) || Array.isArray(subAppId)) return

    await flowConfigStore.selectFlow(subAppId, flow)

    await router.push({
      name: 'configurator',
      params: { subAppId: route.params['subAppId'], type: flow.type },
    })
  }

  function clearImage() {
    localSubApp.value.image = undefined
    localSubApp.value.imageSrc = ''
    fileStore.imagePreviewUrl = null
  }

  function getStringParam(param: string | string[] | undefined): string {
    if (typeof param === 'string') return param
    throw new Error(`Route param is undefined`)
  }

  watch(selectedCustomer, customer => {
    localSubApp.value.customerId = customer?.id ?? null
  })

  watch(
    homeConfig,
    () => {
      localSubApp.value.homeConfig = homeConfig.value.map(value => value.widget)
    },
    { deep: true }
  )

  watch(
    menuConfig,
    () => {
      localSubApp.value.menuConfig = menuConfig.value.map(value => value.widget)
    },
    { deep: true }
  )

  watch(
    localSubApp,
    async current => {
      if (!originalPayload.value) {
        hasChanges.value = true
        return
      }

      const original = originalPayload.value

      const imageChanged = current.image
        ? !original.image || (await isBlobDifferentFromFile(original.image, current.image))
        : true

      const cssVariablesChanged = !areCssVariablesEqual(current.cssVariables, original.cssVariables)

      hasChanges.value =
        current.name !== original.name ||
        current.customerId !== original.customerId ||
        current.cluster !== original.cluster ||
        imageChanged ||
        current.intent !== original.intent ||
        current.visible !== original.visible ||
        current.legacyId !== original.legacyId ||
        current.legacyRegistrationEndpoint !== original.legacyRegistrationEndpoint ||
        current.homeConfig.length !== original.homeConfig.length ||
        current.homeConfig.some(
          (item, index) => JSON.stringify(item) !== JSON.stringify(original.homeConfig[index])
        ) ||
        current.menuConfig.length !== original.menuConfig.length ||
        current.menuConfig.some(
          (item, index) => JSON.stringify(item) !== JSON.stringify(original.menuConfig[index])
        ) ||
        cssVariablesChanged
    },
    { deep: true }
  )

  function areCssVariablesEqual(current: Record<string, string>, original: Record<string, string>) {
    const currentKeys = Object.keys(current)
    const originalKeys = Object.keys(original)
    if (currentKeys.length !== originalKeys.length) return false
    return currentKeys.every(key => current[key] === original[key])
  }

  onMounted(async () => {
    await customerStore.fetchCustomers()

    if (route.params['subAppId'] === 'new') {
      localSubApp.value = createEmptySubAppPayload()
    } else {
      const subApp = await subAppStore.fetchSubApp(getStringParam(route.params['subAppId']))
      subApp.cssVariables = withDefaultColours(subApp.cssVariables)

      localSubApp.value = subApp
      originalPayload.value = {
        ...subApp,
        homeConfig: [...subApp.homeConfig],
        menuConfig: [...subApp.menuConfig],
        cssVariables: { ...subApp.cssVariables },
      }
      hasTranslations.value = !!subApp.translationsFileId
      selectedCustomer.value =
        customerStore.customers.find(customer => customer.id === subApp.customerId) ?? null
      homeConfig.value = localSubApp.value.homeConfig.map(widget => ({ widget }))
      menuConfig.value = localSubApp.value.menuConfig.map(widget => ({ widget }))
      const flows = await flowConfigStore.fetchFlows(require(subApp.id))

      for (const flow of flows) {
        if (availableFlows.value.some(availableFlow => availableFlow.type === flow.type)) continue

        availableFlows.value.push({
          type: flow.type,
          label: flow.label,
          description: flow.description,
        })
      }
    }
  })
</script>

<template>
  <TopBar />
  <SubAppThemeModal
    v-if="localSubApp.id"
    v-model="showSubAppThemeModal"
    v-model:home-config="homeConfig"
    v-model:menu-config="menuConfig"
    v-model:css-variables="localSubApp.cssVariables"
    :subAppId="localSubApp.id"
    :save="submitSubApp"
    :is-submit-disabled="isSubmitDisabled"
    :saved-css-variables="originalPayload?.cssVariables ?? {}"
  />
  <NewFlowModal v-if="localSubApp.id" v-model:show="showNewFlowModal" :subAppId="localSubApp.id" />
  <ImportFlowModal
    v-if="localSubApp.id"
    v-model:show="showImportFlowModal"
    :subAppId="localSubApp.id"
  />
  <TranslationsModal
    v-if="localSubApp.id"
    v-model:show="showTranslationsModal"
    v-model:has-translations="hasTranslations"
    :subAppId="localSubApp.id"
  />
  <div class="center">
    <div class="details-container">
      <div class="sub-app-container">
        <FormInput v-model="localSubApp.name" label="Name" :error="visibleErrors.name" />
        <DropDown
          v-model="selectedCustomer"
          :data="customerStore.customers"
          :error="visibleErrors.customerId"
          label="Organization"
          :enableSearch="true"
          required
        >
          <template #item="{ item }">{{ item.name }} {{ item.email }}</template>
        </DropDown>
        <FormInput v-model="localSubApp.cluster" label="Group" :error="visibleErrors.cluster" />
        <FormInput
          v-model="localSubApp.legacyId"
          label="Legacy ID"
          optional
          :error="visibleErrors.legacyId"
        />
        <FormInput
          v-model="localSubApp.legacyRegistrationEndpoint"
          label="Legacy Registration Endpoint"
          optional
        />
        <FormInput v-model="localSubApp.intent" label="Intent link" optional />

        <FormFieldContainer label="Visible">
          <SwitchButton v-model="localSubApp.visible" size="md" />
        </FormFieldContainer>

        <FormFieldContainer label="Logo" :error="visibleErrors.image">
          <template v-if="hasImage" #action-button>
            <UiButton
              class="delete-btn"
              icon="cross"
              size="small"
              variant="negative"
              @click="clearImage()"
            />
          </template>
          <div v-if="isLoadingPreview" class="loading">
            <LoadingSpinner />
          </div>
          <FileUpload
            v-else
            v-model="localSubApp.image"
            :initialPreviewUrl="localSubApp.imageSrc || fileStore.imagePreviewUrl"
          />
        </FormFieldContainer>

        <UiButton
          v-if="localSubApp.id"
          label="Configuration"
          @click="showSubAppThemeModal = true"
        />
        <UiButton
          class="save-button"
          label="Opslaan"
          variant="prominent"
          :disabled="isSubmitDisabled"
          @click="submitSubApp()"
        />
      </div>
      <div class="divider" />
      <div class="action-container">
        <div class="ui-buttons">
          <UiButton
            label="Translations"
            :disabled="!localSubApp.id"
            @click="showTranslationsModal = true"
          />
          <UiButton icon="download" @click="showImportFlowModal = true" />
          <UiButton icon="plus" variant="prominent" @click="showNewFlowModal = true" />
        </div>
        <div
          class="action-row"
          :class="{ disabled: isDisabled }"
          v-for="flow in availableFlows"
          :key="flow.type"
          @click="!localSubApp.intent && onSelect(flow)"
        >
          <div class="action-content">
            <h1 class="flow-label">{{ xPathAction.getValue(flow.label) }}</h1>
            <div class="flow-description">
              {{ xPathAction.getValue(flow.description) }}
            </div>
          </div>
          <div class="arrow">
            <ChevronRight />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="css">
  .loading {
    align-self: center;
  }

  .ui-buttons {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
  }

  .center {
    overflow: auto;
    display: flex;
    flex-grow: 1;
    align-items: center;
  }

  .divider {
    width: 1px;
    align-self: stretch;
    background-color: var(--colours-grey-3);
  }

  .save-button {
    margin-top: var(--spacing-m);
  }

  .flow-label {
    color: var(--colours-grey-5);
  }

  .action-content {
    color: var(--colours-grey-4);
  }

  .action-row {
    width: 100%;
    display: flex;
    flex-direction: row;
    border: 1px solid var(--colours-grey-3);
    border-radius: 0.75rem;
    padding: var(--spacing-s);
    background-color: var(--colours-grey-1);
    cursor: pointer;
  }

  .action-row:hover {
    margin: -1px;
    border: 2px solid var(--colours-support-keyboard-focus);
  }

  .action-row.disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }

  .action-content {
    flex: 1;
  }

  .arrow {
    padding-left: var(--spacing-s);
    align-content: center;
  }

  .details-container {
    height: 100%;
    padding: 0 var(--spacing-l);
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: start;
    flex: 1;
    gap: var(--spacing-m);
  }

  .sub-app-container {
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: var(--spacing-m);
  }

  .action-container {
    align-items: end;
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: var(--spacing-m);
    gap: var(--spacing-m);
  }
</style>
